import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToLastTVView } from "../utils/tvNavigation";
import {
  ArrowRight,
  Calendar,
  Check,
  Clock,
  Code,
  Copy,
  CreditCard,
  Edit,
  Eye,
  ExternalLink,
  FileText,
  ImageIcon,
  Loader2,
  Mail,
  Moon,
  Save,
  Settings,
  Shield,
  Sun,
  Trash2,
  Tv,
  UserPlus,
  Users,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../config/firebase";
import { getMasjidEvents } from "../services/api";
import ImageUploadDialog from "../components/TeleView/ImageUploadDialog";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const MASJID_API_URL = import.meta.env.VITE_MASJID_API;

export default function MasjidDashboard() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const [masjidInfo, setMasjidInfo] = useState(null);
  const [stats, setStats] = useState({ eventsCount: 0, adminsCount: 0 });

  // Widget state
  const [widgetToken, setWidgetToken] = useState(null);
  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState("light");
  const [widgetColor, setWidgetColor] = useState("#bf9a2f");
  const [widgetSize, setWidgetSize] = useState("medium"); // small, medium, large, custom
  const [widgetHeight, setWidgetHeight] = useState(400);

  const HEIGHT_PRESETS = {
    small: 250,
    medium: 400,
    large: 500,
  };
  const normalizeHeight = (val, fallback) => {
    const num = Number(val);
    if (Number.isFinite(num) && num > 0) {
      return Math.max(200, num);
    }
    return fallback;
  };
  const resolveHeight = (sizeValue, customHeightValue) => {
    if (sizeValue === "custom") {
      return normalizeHeight(customHeightValue, HEIGHT_PRESETS.medium);
    }
    return HEIGHT_PRESETS[sizeValue] || HEIGHT_PRESETS.medium;
  };

  // Admin management state
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminOwners, setAdminOwners] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("Admin");
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);

  // Masjid edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Image upload dialog state
  const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);

  const fetchMasjidData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const masjidResponse = await fetch(
        `${API_BASE_URL}/v1/masjid/admin/info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!masjidResponse.ok) {
        let message = "Failed to fetch masjid info";
        let code = "";
        try {
          const contentType = masjidResponse.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const errorData = await masjidResponse.json();
            message = errorData.message || errorData.error || message;
            code = errorData.code || "";
          } else {
            const text = await masjidResponse.text();
            console.error("Non-JSON error response for masjid info:", text);
          }
        } catch (parseErr) {
          console.warn("Error parsing masjid info error response:", parseErr);
        }

        // Allow access even if backend flags signup incomplete
        if (masjidResponse.status === 403 && code === "SIGNUP_INCOMPLETE") {
          setMasjidInfo({});
          setLoading(false);
          return;
        }
        if (masjidResponse.status === 404 && code === "MASJID_NOT_ASSIGNED") {
          navigate("/masjid-signup");
          return;
        }
        throw new Error(message);
      }

      const masjidData = await masjidResponse.json();
      setMasjidInfo(masjidData);

      // Initialize edit data with masjid info
      const m = masjidData.masjid;
      if (m) {
        setEditData({
          name: m.name || "",
          nickname: m.nickname || "",
          address: m.address || "",
          city: m.city || "",
          state: m.state || "",
          zip: m.zip || "",
          website: m.website || "",
        });
      }

      const masjidId = masjidData.masjid?.id;

      if (masjidId) {
        try {
          const adminsResponse = await fetch(
            `${API_BASE_URL}/v1/masjid/${masjidId}/admins`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (adminsResponse.ok) {
            const adminsData = await adminsResponse.json();
            const totalAdmins =
              (adminsData.owners?.length || 0) +
              (adminsData.admins?.length || 0);
            setStats((prev) => ({ ...prev, adminsCount: totalAdmins }));
          }
        } catch (adminError) {
          console.warn("Failed to fetch admins count:", adminError);
        }

        try {
          const eventsData = await getMasjidEvents(masjidId);
          const eventsCount =
            eventsData?.count ?? eventsData?.events?.length ?? 0;
          setStats((prev) => ({ ...prev, eventsCount }));
        } catch (eventsError) {
          console.warn("Failed to fetch events count:", eventsError);
        }
      }
    } catch (err) {
      console.error("Error fetching masjid data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMasjidData();
  }, [fetchMasjidData]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your dashboard...</p>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Unable to load dashboard</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchMasjidData}>Try Again</Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const masjid = masjidInfo?.masjid;
  const subscription = masjid?.subscription || "basic";
  const isPro = subscription === "pro";
  const adminRole = userProfile?.adminRole || masjidInfo?.adminRole || "Admin";
  const isOwner =
    adminRole?.toLowerCase() === "admin owner" ||
    adminRole?.toLowerCase() === "super";

  const handleUpgradePlan = async () => {
    if (!user || isPro) return;
    try {
      setUpgradeLoading(true);
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${MASJID_API_URL}/v1/masjid/billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error("Failed to create masjid checkout session");
        window.alert(
          "Unable to start subscription checkout. Please try again or contact support."
        );
        return;
      }

      const data = await response.json();
      if (data.ok && data.url) {
        window.location.href = data.url;
      } else {
        console.error(
          "Unexpected response from masjid checkout endpoint:",
          data
        );
        window.alert(
          "Unable to start subscription checkout. Please try again."
        );
      }
    } catch (err) {
      console.error("Error creating masjid checkout session:", err);
      window.alert("Unable to start subscription checkout. Please try again.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Widget functions
  const generateWidgetToken = async () => {
    try {
      setGeneratingToken(true);

      if (!auth.currentUser) {
        setGeneratingToken(false);
        return null;
      }

      const token = await auth.currentUser.getIdToken(true);
      // Try to get masjid ID from masjidInfo first, then fallback to userProfile
      const masjidId =
        masjidInfo?.masjid?.id ||
        userProfile?.masjidId ||
        userProfile?.activeMasjidId;

      if (!masjidId) {
        setGeneratingToken(false);
        return null;
      }

      const response = await fetch(
        `${API_BASE_URL}/v1/widget-token/${masjidId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || "Failed to generate widget token");
      }

      setWidgetToken(data.token);
      setGeneratingToken(false);
      return data.token;
    } catch (err) {
      console.error("Error generating widget token:", err);
      setGeneratingToken(false);
      return null;
    }
  };

  const handleShowWidget = () => {
    try {
      // Try to get masjid ID from masjidInfo first, then fallback to userProfile
      const masjidId =
        masjidInfo?.masjid?.id ||
        userProfile?.masjidId ||
        userProfile?.activeMasjidId;

      if (!masjidId) {
        window.alert(
          "Unable to load widget. Please ensure your masjid information is complete."
        );
        return;
      }

      // Navigate to the widget hub page
      navigate("/widgets");
    } catch (error) {
      console.error("Error in handleShowWidget:", error);
      window.alert("An error occurred. Please try again.");
    }
  };

  const getWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = widgetColor.startsWith("#")
      ? widgetColor.substring(1)
      : widgetColor;
    const height = resolveHeight(widgetSize, widgetHeight);
    const heightQuery = height ? `&height=${height}` : "";
    return `${window.location.origin}/widget?token=${widgetToken}&theme=${widgetTheme}&color=${colorParam}&size=${widgetSize}${heightQuery}`;
  };

  const getSizeStyles = () => {
    const height = resolveHeight(widgetSize, widgetHeight);
    const width = Math.max(220, Math.round((height * 9) / 16));

    return {
      maxWidth: `${width}px`,
      height: `${height}px`,
    };
  };

  const getEmbedCode = (widgetUrl) => {
    const sizeStyles = getSizeStyles();
    const minHeight = sizeStyles.minHeight
      ? ` min-height: ${sizeStyles.minHeight};`
      : "";
    return `<!-- Muslim+ Prayer Times Widget -->\n<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: none; width: 100%; max-width: ${sizeStyles.maxWidth}; height: ${sizeStyles.height};${minHeight} display: block; margin: 0 auto;"></iframe>\n<!-- End Muslim+ Widget -->`;
  };

  const handleSizeSelect = (size) => {
    setWidgetSize(size);
    if (size !== "custom") {
      setWidgetHeight(resolveHeight(size, widgetHeight));
    }
  };

  const copyWidgetCode = async () => {
    try {
      const widgetUrl = getWidgetUrl();
      if (!widgetUrl) return;

      if (widgetSize === "custom") {
        const num = Number(widgetHeight);
        if (!Number.isFinite(num) || num < 200) {
          window.alert("Please enter a valid height (200px or greater) before copying.");
          return;
        }
      }

      const code = getEmbedCode(widgetUrl);

      await navigator.clipboard.writeText(code);
      setCopiedWidget(true);
      setTimeout(() => setCopiedWidget(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Admin management functions
  const loadAdmins = async () => {
    const masjidId = masjidInfo?.masjid?.id;
    if (!masjidId || !user) return;

    setAdminsLoading(true);
    setAdminError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/v1/masjid/${masjidId}/admins`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load admins");
      }

      const data = await response.json();
      setAdminOwners(Array.isArray(data?.owners) ? data.owners : []);
      setAdmins(Array.isArray(data?.admins) ? data.admins : []);
    } catch (e) {
      console.error("load admins failed:", e);
      setAdminOwners([]);
      setAdmins([]);
      setAdminError("Failed to load admins");
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleShowAdmins = async () => {
    setAdminsDialogOpen(true);
    await loadAdmins();
  };

  const addAdmin = async () => {
    const email = (newAdminEmail || "").trim().toLowerCase();
    if (!email) return;

    const masjidId = masjidInfo?.masjid?.id;
    if (!masjidId) return;

    setAdminSaving(true);
    setAdminError(null);
    setAdminSuccess(null);

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await fetch(
        `${API_BASE_URL}/v1/masjid/${masjidId}/admins`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, role: newAdminRole }),
        }
      );

      if (response.status === 409) {
        setAdminError(`${email} is already an admin for this masjid.`);
        return;
      }

      if (response.status === 404) {
        setAdminError(
          `The email "${email}" is not registered. Please ask them to create an account first.`
        );
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to add admin");
      }

      setNewAdminEmail("");
      setNewAdminRole("Admin");
      setAdminSuccess(
        `${email} has been added as ${
          newAdminRole === "Admin Owner" ? "an Admin Owner" : "an Admin"
        }.`
      );
      setTimeout(() => setAdminSuccess(null), 3000);
      await loadAdmins();
      // Update stats
      setStats((prev) => ({ ...prev, adminsCount: prev.adminsCount + 1 }));
    } catch (e) {
      console.error("Add admin error:", e);
      setAdminError(e.message || "Failed to add admin. Please try again.");
    } finally {
      setAdminSaving(false);
    }
  };

  const removeAdmin = async (email) => {
    const masjidId = masjidInfo?.masjid?.id;
    if (!masjidId) return;

    setAdminSaving(true);
    setAdminError(null);

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await fetch(
        `${API_BASE_URL}/v1/masjid/${masjidId}/admins/${encodeURIComponent(
          email.toLowerCase()
        )}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to remove admin");
      }

      setAdminSuccess(`${email} has been removed.`);
      setTimeout(() => setAdminSuccess(null), 3000);
      await loadAdmins();
      // Update stats
      setStats((prev) => ({
        ...prev,
        adminsCount: Math.max(0, prev.adminsCount - 1),
      }));
    } catch (e) {
      console.error("Remove admin error:", e);
      setAdminError(e.message || "Failed to remove admin");
    } finally {
      setAdminSaving(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    try {
      const date = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Handle saving masjid edits
  const handleSaveEdit = async () => {
    const masjidId = masjidInfo?.masjid?.id;
    if (!masjidId) return;

    try {
      setEditSaving(true);

      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error("Authentication required");

      // Prepare updates - only include changed fields
      const m = masjidInfo?.masjid || {};
      const updates = {};
      Object.keys(editData).forEach((key) => {
        if (editData[key] !== m[key]) {
          updates[key] = editData[key];
        }
      });

      if (Object.keys(updates).length === 0) {
        setEditDialogOpen(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/v1/masjid/${masjidId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to update masjid");
      }

      setEditDialogOpen(false);
      // Refresh masjid data
      await fetchMasjidData();
    } catch (err) {
      console.error("Error saving masjid info:", err);
    } finally {
      setEditSaving(false);
    }
  };

  const quickActions = [
    {
      icon: FileText,
      title: "Monthly PDF",
      description: isPro
        ? "Generate a printable monthly prayer times PDF"
        : "Generate a monthly prayer times PDF (Pro only)",
      route: "/monthly-times",
      gradient: "from-sky-500/20 to-blue-500/10",
      iconBg: "bg-sky-500/10",
      iconColor: "text-sky-500",
      requiresPro: true,
    },
    {
      icon: Clock,
      title: "Prayer Times",
      description: "Manage daily prayer schedules",
      route: "/prayer-times",
      gradient: "from-emerald-500/20 to-green-500/10",
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-500",
    },
    {
      icon: Calendar,
      title: "Events",
      description: "Create and manage community events",
      route: "/events",
      gradient: "from-purple-500/20 to-pink-500/10",
      iconBg: "bg-purple-500/10",
      iconColor: "text-purple-500",
    },
    {
      icon: Code,
      title: "Website Widgets",
      description: "Embed prayer times, calendars, and events on your website",
      customAction: handleShowWidget,
      gradient: "from-orange-500/20 to-amber-500/10",
      iconBg: "bg-orange-500/10",
      iconColor: "text-orange-500",
    },
    {
      icon: ImageIcon,
      title: "Slideshow Images",
      description: "Manage TV display slideshow images",
      customAction: () => setImageUploadDialogOpen(true),
      gradient: "from-blue-500/20 to-cyan-500/10",
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-500",
    },
    {
      icon: Users,
      title: "Admins",
      description: isOwner
        ? "Manage team members and permissions"
        : "Admin Owner access required",
      customAction: isOwner ? handleShowAdmins : null,
      gradient: "from-indigo-500/20 to-violet-500/10",
      iconBg: "bg-indigo-500/10",
      iconColor: "text-indigo-500",
      requiresOwner: true,
    },
    {
      icon: Settings,
      title: "Settings",
      description: "Configure account settings",
      route: "/settings",
      gradient: "from-slate-500/20 to-gray-500/10",
      iconBg: "bg-slate-500/10",
      iconColor: "text-slate-500",
      fullWidth: true,
    },
  ];

  const handleQuickActionClick = async (action) => {
    if (action.requiresPro && !isPro) {
      // Simple, unobtrusive upgrade prompt
      window.alert(
        "Monthly PDF generation is a Pro feature.\n\nGo to the Subscription option in your sidebar to upgrade your plan."
      );
      return;
    }
    if (action.requiresOwner && !isOwner) {
      // Blocked for non-owner admins
      return;
    }
    if (action.customAction) {
      await action.customAction();
      return;
    }
    navigate(action.route);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Upgrade banner (basic only) / Pro status */}
        {!isPro ? (
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="flex flex-col items-start justify-between gap-4 p-6 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                  <CreditCard className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Unlock Pro features</h3>
                    <Badge variant="secondary">Basic</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Monthly PDFs, priority support, and more.
                  </p>
                </div>
              </div>
              <Button onClick={handleUpgradePlan} disabled={upgradeLoading}>
                {upgradeLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Upgrade Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="flex items-center gap-3 p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                <Shield className="h-5 w-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Pro plan active</h3>
                  <Badge>Pro</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  All premium features are unlocked for this masjid.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Masjid Info */}
        {masjid && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Masjid Information</CardTitle>
                <CardDescription>
                  Your masjid's profile and contact details
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Name
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {masjid.name}
                  </p>
                </div>

                {masjid.nickname ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Nickname
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">
                      {masjid.nickname}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Location
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">
                      {masjid.city}, {masjid.state} {masjid.zip}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Address
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {masjid.address || "Not set"}
                  </p>
                </div>

                {masjid.nickname ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Location
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">
                      {masjid.city}, {masjid.state} {masjid.zip}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Website
                    </p>
                    <p className="mt-1 truncate text-base font-medium text-foreground">
                      {masjid.website || "Not set"}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="bg-gradient-to-br from-emerald-500/20 to-green-500/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10">
                  <Calendar className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Events
                </p>
                <p className="mt-1 text-3xl font-bold text-foreground">
                  {stats.eventsCount}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Upcoming and recent gatherings
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-500/20 to-violet-500/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-500/10">
                  <Users className="h-6 w-6 text-indigo-500" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Admins
                </p>
                <p className="mt-1 text-3xl font-bold text-foreground">
                  {stats.adminsCount}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Team members with access
                </p>
              </div>
            </CardContent>
          </Card>

          <Card
            className="group cursor-pointer bg-gradient-to-br from-amber-500/20 to-orange-500/10 transition hover:-translate-y-1 hover:shadow-lg"
            onClick={() => navigateToLastTVView(navigate)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                  <Tv className="h-6 w-6 text-amber-500" />
                </div>
                <ArrowRight className="h-5 w-5 text-amber-500 transition-transform group-hover:translate-x-1" />
              </div>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  TV Display
                </p>
                <p className="mt-1 text-3xl font-bold text-foreground">Launch</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Open the live display
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Tools Grid */}
        <div>
          <div className="mb-4">
            <h2 className="text-2xl font-semibold tracking-tight">Admin Tools</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage prayer times, events, slideshow images, embeds, and your team.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const isProDisabled = action.requiresPro && !isPro;
              const isOwnerDisabled = action.requiresOwner && !isOwner;
              const isDisabled = isProDisabled || isOwnerDisabled;
              return (
                <Card
                  key={action.title}
                  onClick={() => !isDisabled && handleQuickActionClick(action)}
                  className={`bg-gradient-to-br ${action.gradient} transition ${
                    isDisabled
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:-translate-y-1 hover:shadow-lg"
                  } ${action.fullWidth ? "md:col-span-2 lg:col-span-3" : ""}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${action.iconBg}`}
                      >
                        <Icon className={`h-6 w-6 ${action.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-semibold text-foreground">
                          {action.title}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {action.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Widget Dialog */}
      <Dialog open={widgetDialogOpen} onOpenChange={setWidgetDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[95vh] flex flex-col">
          <DialogHeader className="shrink-0 pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Code className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl">
                  Prayer Times Widget
                </DialogTitle>
                <DialogDescription className="mt-1.5">
                  Customize your widget and copy the embed code to add it to
                  your website. No technical knowledge required!
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 overflow-y-auto flex-1 min-h-0 pr-2 py-4">
            {/* Customization Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  Customization
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={widgetTheme === "light" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWidgetTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={widgetTheme === "dark" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWidgetTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="widgetColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="widgetColor"
                      type="color"
                      value={widgetColor}
                      onChange={(e) => setWidgetColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={widgetColor}
                      onChange={(e) => setWidgetColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Size */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Size</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={widgetSize === "small" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSizeSelect("small")}
                      className="text-xs"
                    >
                      Small (250px)
                    </Button>
                    <Button
                      variant={widgetSize === "medium" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSizeSelect("medium")}
                      className="text-xs"
                    >
                      Medium (400px)
                    </Button>
                    <Button
                      variant={widgetSize === "large" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSizeSelect("large")}
                      className="text-xs"
                    >
                      Large (500px)
                    </Button>
                    <Button
                      variant={
                        widgetSize === "custom" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => handleSizeSelect("custom")}
                      className="text-xs"
                    >
                      Custom
                    </Button>
                  </div>
                  {widgetSize === "custom" && (
                    <div className="space-y-1">
                      <Label
                        className="text-xs text-muted-foreground"
                        htmlFor="customHeight"
                      >
                        Custom height (px)
                      </Label>
                      <Input
                        id="customHeight"
                        type="text"
                        value={widgetHeight}
                        onChange={(e) => setWidgetHeight(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Live Preview</Label>
              </div>
              <div
                className="rounded-lg border bg-muted/30 overflow-hidden"
                style={{ height: "560px", minHeight: "420px" }}
              >
                {widgetToken ? (
                  <PreviewIframe
                    key={`${widgetToken}-${widgetTheme}-${widgetColor}-${widgetSize}-${widgetHeight}`}
                    src={getWidgetUrl()}
                    size={widgetSize}
                    targetHeight={resolveHeight(widgetSize, widgetHeight)}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    {generatingToken ? (
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    ) : (
                      <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Widget token will be generated automatically
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Preview will appear here once ready
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Embed Code Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between pb-2 border-b">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Embed Code</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={copyWidgetCode}
                    disabled={!widgetToken}
                    className="gap-2"
                  >
                    {copiedWidget ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="rounded-lg bg-muted/50 border p-4 relative">
                {!widgetToken ? (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-sm text-muted-foreground">
                      Generating widget token...
                    </p>
                  </div>
                ) : (
                  <>
                    <code className="text-xs break-all whitespace-pre-wrap font-mono block overflow-x-auto">
                      {(() => {
                        const widgetUrl = getWidgetUrl();
                          return getEmbedCode(widgetUrl);
                      })()}
                    </code>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span>💡</span>
                <span>
                  Simply copy the code above and paste it into your website's
                  HTML where you want the widget to appear.
                </span>
              </p>
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-4 border-t">
            <div className="flex items-center gap-2 w-full justify-between">
              {widgetToken && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const widgetUrl = getWidgetUrl();
                    if (widgetUrl) {
                      window.open(widgetUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in New Tab
                </Button>
              )}
              <Button
                onClick={() => setWidgetDialogOpen(false)}
                className={widgetToken ? "" : "ml-auto"}
              >
                Done
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Management Dialog */}
      <Dialog open={adminsDialogOpen} onOpenChange={setAdminsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle>Manage Admins</DialogTitle>
                {masjid?.name && (
                  <DialogDescription className="mt-0.5">
                    {masjid.name}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-4 overflow-hidden flex-1">
            {/* Add Admin Section */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-3 shrink-0">
              <Label className="text-sm font-medium">Add Admin</Label>

              {/* Role Selector - Only for Super Admins */}
              {adminRole?.toLowerCase() === "super" && (
                <div className="flex gap-2">
                  <Button
                    variant={newAdminRole === "Admin" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewAdminRole("Admin")}
                    className="flex-1"
                  >
                    {newAdminRole === "Admin" && (
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Regular Admin
                  </Button>
                  <Button
                    variant={
                      newAdminRole === "Admin Owner" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setNewAdminRole("Admin Owner")}
                    className="flex-1"
                  >
                    {newAdminRole === "Admin Owner" && (
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Admin Owner
                  </Button>
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addAdmin()}
                    className="pl-9"
                    disabled={adminSaving}
                  />
                </div>
                <Button
                  onClick={addAdmin}
                  disabled={adminSaving || !newAdminEmail.trim()}
                  className="gap-1.5"
                >
                  {adminSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Grant admin access to manage this masjid
              </p>
            </div>

            {/* Error/Success Messages */}
            {adminError && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive shrink-0">
                {adminError}
              </div>
            )}
            {adminSuccess && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-sm text-emerald-600 dark:text-emerald-400 shrink-0">
                {adminSuccess}
              </div>
            )}

            {/* Admin List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {adminsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Loading admins...
                  </p>
                </div>
              ) : adminOwners.length === 0 && admins.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium">No admins yet</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Add admins to help manage your masjid
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Admin Owners Section */}
                  {adminOwners.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admin Owners
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {adminOwners.map(({ email, createdAt }) => (
                          <div
                            key={email}
                            className="flex items-center gap-3 rounded-lg border bg-card p-3"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                              <Shield className="h-4 w-4 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium">
                                  {email}
                                </p>
                                {email === user?.email && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                    You
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Owner • Added {formatTimestamp(createdAt)}
                              </p>
                            </div>
                            {adminRole?.toLowerCase() === "super" &&
                              email !== user?.email && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Remove ${email} as an Admin Owner?`
                                      )
                                    ) {
                                      removeAdmin(email);
                                    }
                                  }}
                                  disabled={adminSaving}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regular Admins Section */}
                  {admins.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admins
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {admins.map(({ email, createdAt }) => (
                          <div
                            key={email}
                            className="flex items-center gap-3 rounded-lg border bg-card p-3"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-sm font-medium">
                                {email}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Added {formatTimestamp(createdAt)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                if (
                                  window.confirm(`Remove ${email} as an Admin?`)
                                ) {
                                  removeAdmin(email);
                                }
                              }}
                              disabled={adminSaving}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-4">
            <Button
              variant="outline"
              onClick={() => setAdminsDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <ImageUploadDialog
        masjidId={masjid?.id}
        open={imageUploadDialogOpen}
        onOpenChange={setImageUploadDialogOpen}
      />

      {/* Edit Masjid Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Edit Masjid Information</DialogTitle>
            <DialogDescription>
              Update your masjid's information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Masjid Name</Label>
              <Input
                id="edit-name"
                value={editData.name || ""}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-nickname">Nickname (Optional)</Label>
              <Input
                id="edit-nickname"
                value={editData.nickname || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 15) {
                    setEditData({ ...editData, nickname: value });
                  }
                }}
                placeholder="Short name (max 15 characters)"
                maxLength={15}
              />
              {editData.nickname && (
                <p className="text-xs text-muted-foreground">
                  {editData.nickname.length}/15 characters
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={editData.address || ""}
                onChange={(e) =>
                  setEditData({ ...editData, address: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-city">City</Label>
              <Input
                id="edit-city"
                value={editData.city || ""}
                onChange={(e) =>
                  setEditData({ ...editData, city: e.target.value })
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  value={editData.state || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, state: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-zip">ZIP Code</Label>
                <Input
                  id="edit-zip"
                  value={editData.zip || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, zip: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-website">Website</Label>
              <Input
                id="edit-website"
                value={editData.website || ""}
                onChange={(e) =>
                  setEditData({ ...editData, website: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter className="shrink-0 pt-4">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={editSaving}>
              {editSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// Auto-scaling iframe preview component
function PreviewIframe({ src, size = "medium", targetHeight }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });

  useEffect(() => {
    const compute = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();

      const heightMap = { small: 250, medium: 400, large: 500 };
      const rawHeight = targetHeight || heightMap[size] || heightMap.medium;
      const height = rawHeight;
      const width = Math.max(220, Math.round((rawHeight * 9) / 16));

      const sW = rect.width / width;
      const sH = rect.height / height;
      const s = Math.min(sW, sH);
      const clamped = Math.max(0.35, Math.min(s, 1));

      setScale(clamped);
      setDimensions({ width, height });
    };

    compute();
    const handle = () => compute();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [size, targetHeight]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflow: "hidden",
        padding: "16px",
      }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
        }}
      >
        <iframe
          src={src}
          width={dimensions.width}
          style={{
            border: "none",
            borderRadius: "8px",
            height: `${dimensions.height}px`,
          }}
          title="Prayer Times Widget Preview"
        />
      </div>
    </div>
  );
}
